import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { Input as AntInput, Modal, Checkbox } from "antd";
import { ICountry, LocationFormValues } from "../../../types/api/location";
import { useCountryCreate, useCountryUpdate } from "../../../hooks/api/location/hooks";
import { useMessageApi } from "../../../provider/AntdNotification";

interface CountryModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedItem?: ICountry | null;
  qData?: any;
}

const CountryModal: React.FC<CountryModalProps> = ({
  isOpen,
  onClose,
  selectedItem,
  qData,
}) => {
  const messageApi = useMessageApi();
  const { control, handleSubmit, reset, setValue } = useForm<LocationFormValues>({
    defaultValues: { name: "", slug: "", status: true },
  });

  const createMutation = useCountryCreate({
    qData,
    onSuccess: () => {
      messageApi.success("Country created successfully");
      onClose();
    },
    onError: (err: any) =>
      messageApi.error(err?.response?.data?.message || err?.message || "Failed to create country"),
  });
  const updateMutation = useCountryUpdate({
    qData,
    onSuccess: () => {
      messageApi.success("Country updated successfully");
      onClose();
    },
    onError: (err: any) =>
      messageApi.error(err?.response?.data?.message || err?.message || "Failed to update country"),
  });

  useEffect(() => {
    if (isOpen) {
      if (selectedItem) {
        reset({
          name: selectedItem.name ?? "",
          slug: selectedItem.slug ?? "",
          status: selectedItem.status ?? true,
        });
      } else {
        reset({ name: "", slug: "", status: true });
      }
    }
  }, [isOpen, selectedItem, reset]);

  const onSubmit = (data: LocationFormValues) => {
    if (selectedItem) {
      updateMutation.mutate({ id: selectedItem._id, payload: data });
    } else {
      createMutation.mutate(data);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Modal
      title={selectedItem ? "Edit Country" : "Add Country"}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
        <Controller
          name="name"
          control={control}
          rules={{ required: "Name is required" }}
          render={({ field, fieldState }) => (
            <div>
              <label className="block text-sm font-medium mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <AntInput
                {...field}
                placeholder="Enter country name"
                status={fieldState.error ? "error" : ""}
                onChange={(e) => {
                  field.onChange(e);
                  if (!selectedItem) {
                    setValue(
                      "slug",
                      e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, "-")
                        .replace(/(^-|-$)+/g, "")
                    );
                  }
                }}
              />
              {fieldState.error && (
                <p className="text-red-500 text-xs mt-1">{fieldState.error.message}</p>
              )}
            </div>
          )}
        />

        <Controller
          name="slug"
          control={control}
          rules={{ required: "Slug is required" }}
          render={({ field, fieldState }) => (
            <div>
              <label className="block text-sm font-medium mb-1">
                Slug <span className="text-red-500">*</span>
              </label>
              <AntInput {...field} placeholder="country-slug" status={fieldState.error ? "error" : ""} />
              {fieldState.error && (
                <p className="text-red-500 text-xs mt-1">{fieldState.error.message}</p>
              )}
            </div>
          )}
        />

        <Controller
          name="status"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Checkbox checked={value} onChange={(e) => onChange(e.target.checked)}>
              Active Status
            </Checkbox>
          )}
        />

        <div className="flex justify-end gap-2 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-slate-200 rounded-md hover:bg-slate-50 transition-colors"
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-hover transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            disabled={isPending}
          >
            {isPending && (
              <span className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
            )}
            {selectedItem ? "Update" : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CountryModal;
